/*
 * The honest-state disclosure — §4's central voice mechanism, and the one
 * component whose accessibility rules are non-negotiable.
 *
 * THE PATTERN, quoted from §4 because the details are the whole point:
 *
 *   "succinct label always visible; detail on pointer-hover where a pointer
 *   exists; detail on tap everywhere. The tap target is a small disclosure on
 *   the label itself — never a long-press (that's the OS context menu), never a
 *   navigation. Detail text is real DOM content, screen-reader reachable; no
 *   title-attribute tooltips."
 *
 * FIVE THINGS THIS DELIBERATELY DOES NOT DO, each of them a rule:
 *
 *   1. NO `title` ATTRIBUTE. A title tooltip is unreachable by touch, invisible
 *      to most screen readers, and unstyleable. §4 rules it out by name, and the
 *      non-negotiables list repeats it: "real DOM text for all honest-state
 *      details (no title-tooltips)".
 *   2. NO LONG-PRESS. That gesture belongs to the OS context menu, and taking it
 *      would violate principle 1's "platform affordances are sacred".
 *   3. NO NAVIGATION. The disclosure reveals text in place; it is not a link and
 *      does not change the URL, so the back button keeps meaning what it meant.
 *   4. NO HOVER-ONLY REVEAL. Hover is an enhancement for pointer users. The
 *      detail is always reachable by click, tap, and keyboard, because §7 bars
 *      hover-only affordances.
 *   5. NO DIMMING OF THE DETAIL. This is explanatory text — "dim the datum,
 *      never the words" (§6).
 *
 * `aria-expanded` on a real <button>, per build rule 5 and the non-negotiables'
 * "correct ARIA on disclosures and collapse buttons".
 */

import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./HonestState.module.css";

export interface HonestStateProps {
  /** The succinct label. ALWAYS visible — never hidden behind the disclosure. */
  label: string;

  /**
   * The explanation, revealed in place. Real DOM text, so a screen reader
   * reaches it in reading order once expanded.
   */
  detail: string;

  /**
   * Renders the label in amber, for an aging or urgent-but-healthy state. Never
   * red: red is interaction, and an honest state is information rather than an
   * error (§6).
   */
  tone?: "neutral" | "amber";
}

export function HonestState({ label, detail, tone = "neutral" }: HonestStateProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const detailId = useId();

  return (
    <span className={styles.wrap}>
      <span className={tone === "amber" ? styles.labelAmber : styles.label}>
        {label}
      </span>

      <button
        type="button"
        className={styles.disclosure}
        aria-expanded={open}
        aria-controls={detailId}
        // The visible glyph is decorative; this is the accessible name. It says
        // what the control does rather than naming the icon.
        aria-label={t("a11y.moreInfo")}
        onClick={() => setOpen((v) => !v)}
      >
        {/* A small circled "i" drawn in text rather than an icon font or an SVG
            sprite: at this size a glyph is as legible, it inherits the text
            colour for free, and it scales with the user's font settings. */}
        <span aria-hidden="true">i</span>
      </button>

      {/*
        Rendered only when open, and NOT merely hidden with CSS. A
        display:none element is skipped by screen readers anyway, so keeping it
        mounted would buy nothing while making the collapsed DOM heavier on a
        list of many rows.
      */}
      {open && (
        <span id={detailId} className={styles.detail} role="note">
          {detail}
        </span>
      )}
    </span>
  );
}
